import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DrillDown, { Drill } from "../components/DrillDown";
import TopNav from "../components/TopNav";
import { APIProposal, getAllProposals } from "../api/proposals";
import { APICustomer, getAllCustomers } from "../api/customers";
import { formatMoney } from "../utils";
import pageStyle from "./Analytics.module.css";

const DAY = 86400000;
const PERIODS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "12 months" },
  { value: 0, label: "All time" },
];
const STAGES = [
  { status: "LEAD", label: "Lead" },
  { status: "QUOTED", label: "Quoted" },
  { status: "WON", label: "Won" },
  { status: "DONE", label: "Done" },
];

const isWon = (p: APIProposal) => p.status === "WON" || p.status === "DONE";
// Revenue counts on the invoice date when there is one.
const saleDate = (p: APIProposal) => p.invoiceDate || p.createdAt || 0;
const compact = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : formatMoney(n);

function StatTile({
  label,
  value,
  note,
  onClick,
}: {
  label: string;
  value: string;
  note?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={pageStyle.tile}
      onClick={onClick}
      title="Show the records behind this number"
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <div className={pageStyle.tileValue}>{value}</div>
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
    </button>
  );
}

// Single-series column chart: won revenue per month.
type Month = { key: string; label: string; total: number; count: number };

function MonthlyRevenue({
  months,
  onOpen,
}: {
  months: Month[];
  onOpen: (key: string) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(...months.map((m) => m.total), 1);
  // Round the top of the axis to a clean number.
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const ticks = [0, top / 2, top];

  return (
    <section className={pageStyle.card}>
      <div className={pageStyle.cardHeader}>
        <div>
          <Typography variant="subtitle1">Won revenue by month</Typography>
          <Typography variant="body2" color="text.secondary">
            Last 12 months, Won and Done proposals. Click a month to see its
            jobs.
          </Typography>
        </div>
        <Button size="small" onClick={() => setAsTable(!asTable)}>
          {asTable ? "Show chart" : "Show table"}
        </Button>
      </div>
      {asTable ? (
        <table className={pageStyle.table}>
          <thead>
            <tr>
              <th>Month</th>
              <th>Jobs</th>
              <th>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr
                key={m.key}
                className={pageStyle.clickRow}
                onClick={() => onOpen(m.key)}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onOpen(m.key)}
              >
                <td>{m.label}</td>
                <td>{m.count}</td>
                <td>{formatMoney(m.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className={pageStyle.chart}>
          <div className={pageStyle.yAxis}>
            {[...ticks].reverse().map((t) => (
              <span key={t}>{compact(t)}</span>
            ))}
          </div>
          <div className={pageStyle.plot}>
            {ticks.map((t) => (
              <div
                key={t}
                className={pageStyle.gridline}
                style={{ bottom: `${(t / top) * 100}%` }}
              />
            ))}
            <div className={pageStyle.columns}>
              {months.map((m, i) => (
                <button
                  type="button"
                  key={m.key}
                  className={pageStyle.slot}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  onClick={() => onOpen(m.key)}
                  aria-label={`${m.label}: ${formatMoney(m.total)}, ${m.count} jobs. Show jobs`}
                >
                  <div
                    className={pageStyle.column}
                    style={{ height: `${(m.total / top) * 100}%` }}
                  />
                  {hover === i && (
                    <div className={pageStyle.tooltip}>
                      <strong>{m.label}</strong>
                      <div>{formatMoney(m.total)}</div>
                      <div>
                        {m.count} job{m.count === 1 ? "" : "s"}
                      </div>
                      {m.count > 0 && <div className={pageStyle.tooltipHint}>Click to see jobs</div>}
                    </div>
                  )}
                  <span className={pageStyle.xLabel}>{m.label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AnalyticsPage() {
  const [proposals, setProposals] = useState<APIProposal[]>([]);
  const [customers, setCustomers] = useState<APICustomer[]>([]);
  // Period and the open drill-down live in the address, so Back returns
  // to the same list after opening a proposal.
  const [params, setParams] = useSearchParams();
  const period = PERIODS.some((p) => String(p.value) === params.get("period"))
    ? Number(params.get("period"))
    : 90;
  const view = params.get("view") || "";
  const setPeriod = (v: number) => {
    const next = new URLSearchParams(params);
    next.set("period", String(v));
    setParams(next, { replace: true });
  };
  const open = (v: string) => {
    const next = new URLSearchParams(params);
    next.set("period", String(period));
    next.set("view", v);
    setParams(next);
  };
  const close = () => {
    if (window.history.state?.idx > 0) window.history.back();
    else {
      const next = new URLSearchParams(params);
      next.delete("view");
      setParams(next, { replace: true });
    }
  };

  useEffect(() => {
    getAllProposals().then(setProposals);
    getAllCustomers().then(setCustomers);
  }, []);

  const stats = useMemo(() => {
    const since = period ? Date.now() - period * DAY : 0;
    const inPeriod = proposals.filter((p) => (p.createdAt || 0) >= since);
    const wonInPeriod = proposals.filter((p) => isWon(p) && saleDate(p) >= since);
    const wonRevenue = wonInPeriod.reduce((s, p) => s + (p.totalAmount || 0), 0);
    const open = proposals.filter(
      (p) => (p.status === "LEAD" || p.status === "QUOTED") && !p.archivedAt
    );
    const closeRate = inPeriod.length
      ? inPeriod.filter(isWon).length / inPeriod.length
      : 0;
    const newCustomers = customers.filter(
      (c) => (c.createdAt || 0) >= since
    ).length;

    const stages = STAGES.map((s) => {
      const ps = inPeriod.filter((p) => p.status === s.status);
      return {
        ...s,
        count: ps.length,
        total: ps.reduce((sum, p) => sum + (p.totalAmount || 0), 0),
      };
    });

    const byCustomer = new Map<string, { name: string; total: number; jobs: number }>();
    for (const p of wonInPeriod) {
      const e = byCustomer.get(p.customerId) || {
        name: `${p.firstName} ${p.lastName}`.trim() || "Unnamed customer",
        total: 0,
        jobs: 0,
      };
      e.total += p.totalAmount || 0;
      e.jobs++;
      byCustomer.set(p.customerId, e);
    }
    const topCustomers = Array.from(byCustomer.entries())
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 8);

    return {
      since,
      inPeriod,
      wonInPeriod,
      open,
      wonRevenue,
      wonCount: wonInPeriod.length,
      avgDeal: wonInPeriod.length ? wonRevenue / wonInPeriod.length : 0,
      pipeline: open.reduce((s, p) => s + (p.totalAmount || 0), 0),
      openCount: open.length,
      closeRate,
      proposalsCount: inPeriod.length,
      newCustomers,
      stages,
      topCustomers,
    };
  }, [proposals, customers, period]);

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const won = proposals.filter(
        (p) => isWon(p) && saleDate(p) >= d.getTime() && saleDate(p) < next.getTime()
      );
      return {
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString([], { month: "short", year: "numeric" }),
        total: won.reduce((s, p) => s + (p.totalAmount || 0), 0),
        count: won.length,
      };
    });
  }, [proposals]);

  const maxStage = Math.max(...stats.stages.map((s) => s.total), 1);
  const periodLabel = period
    ? `last ${PERIODS.find((p) => p.value === period)?.label}`
    : "all time";

  // What a drill-down key shows.
  const drill = useMemo((): Drill | null => {
    if (!view) return null;
    const [kind, arg] = [view.split(":")[0], view.split(":").slice(1).join(":")];
    const won = { dateOf: saleDate, dateLabel: "Sale date" };
    const made = { dateOf: (p: APIProposal) => p.createdAt || 0, dateLabel: "Created" };
    switch (kind) {
      case "month": {
        const [y, m] = arg.split("-").map(Number);
        const from = new Date(y, m - 1, 1).getTime();
        const to = new Date(y, m, 1).getTime();
        return {
          title: `Won jobs, ${new Date(y, m - 1, 1).toLocaleDateString([], { month: "long", year: "numeric" })}`,
          proposals: proposals.filter((p) => isWon(p) && saleDate(p) >= from && saleDate(p) < to),
          ...won,
        };
      }
      case "won":
        return { title: `Won jobs, ${periodLabel}`, proposals: stats.wonInPeriod, ...won };
      case "proposals":
        return {
          title: `Proposals written, ${periodLabel}`,
          note: "Close rate counts the Won and Done ones among these.",
          proposals: stats.inPeriod,
          ...made,
        };
      case "open":
        return { title: "Open pipeline: lead or quoted, all time", proposals: stats.open, ...made };
      case "stage": {
        const stage = STAGES.find((s) => s.status === arg);
        return {
          title: `${stage?.label || arg} proposals, ${periodLabel}`,
          proposals: stats.inPeriod.filter((p) => p.status === arg),
          ...made,
        };
      }
      case "customer":
        return {
          title: `Won jobs for ${
            stats.topCustomers.find(([id]) => id === arg)?.[1].name || "customer"
          }, ${periodLabel}`,
          proposals: stats.wonInPeriod.filter((p) => p.customerId === arg),
          ...won,
        };
      case "newcustomers":
        return {
          title: `New customers, ${periodLabel}`,
          customers: customers.filter((c) => (c.createdAt || 0) >= stats.since),
        };
      default:
        return null;
    }
  }, [view, proposals, customers, stats, periodLabel]);

  return (
    <div className="page">
      <TopNav pageTitle="Analytics" />
      <main className={pageStyle.main}>
        <div className={pageStyle.content}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={period}
            onChange={(_, v) => v !== null && setPeriod(v)}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.value} value={p.value}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <div className={pageStyle.tiles}>
            <StatTile
              label="Won revenue"
              value={formatMoney(stats.wonRevenue)}
              note={`${stats.wonCount} job${stats.wonCount === 1 ? "" : "s"}`}
              onClick={() => open("won")}
            />
            <StatTile
              label="Close rate"
              value={`${Math.round(stats.closeRate * 100)}%`}
              note={`of ${stats.proposalsCount} proposals`}
              onClick={() => open("proposals")}
            />
            <StatTile
              label="Average won job"
              value={formatMoney(stats.avgDeal)}
              onClick={() => open("won")}
            />
            <StatTile
              label="Open pipeline"
              value={formatMoney(stats.pipeline)}
              note={`${stats.openCount} lead or quoted, all time`}
              onClick={() => open("open")}
            />
            <StatTile
              label="New customers"
              value={String(stats.newCustomers)}
              onClick={() => open("newcustomers")}
            />
            <StatTile
              label="Proposals written"
              value={String(stats.proposalsCount)}
              onClick={() => open("proposals")}
            />
          </div>

          <MonthlyRevenue months={months} onOpen={(key) => open(`month:${key}`)} />

          <div className={pageStyle.twoCol}>
            <section className={pageStyle.card}>
              <Typography variant="subtitle1">Pipeline by stage</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Proposals created in this period
              </Typography>
              {stats.stages.map((s) => (
                <button
                  type="button"
                  key={s.status}
                  onClick={() => open(`stage:${s.status}`)}
                  className={pageStyle.barRow}
                  aria-label={`${s.label}: ${s.count} proposals, ${formatMoney(s.total)}. Show proposals`}
                >
                  <span className={pageStyle.barLabel}>
                    {s.label}
                    <span className={pageStyle.muted}> · {s.count}</span>
                  </span>
                  <span className={pageStyle.barTrack}>
                    <span
                      className={pageStyle.bar}
                      style={{ width: `${(s.total / maxStage) * 100}%` }}
                    />
                  </span>
                  <span className={pageStyle.barValue}>{formatMoney(s.total)}</span>
                </button>
              ))}
            </section>

            <section className={pageStyle.card}>
              <Typography variant="subtitle1">Top customers</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                By won revenue in this period
              </Typography>
              {stats.topCustomers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No won jobs in this period.
                </Typography>
              ) : (
                <table className={pageStyle.table}>
                  <tbody>
                    {stats.topCustomers.map(([id, c]) => (
                      <tr
                        key={id}
                        className={pageStyle.clickRow}
                        onClick={() => open(`customer:${id}`)}
                      >
                        <td>
                          <Link
                            to={`/customers?id=${id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.name}
                          </Link>
                        </td>
                        <td className={pageStyle.muted}>
                          {c.jobs} job{c.jobs === 1 ? "" : "s"}
                        </td>
                        <td>{formatMoney(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </div>
      </main>
      <DrillDown drill={drill} onClose={close} />
    </div>
  );
}

export default AnalyticsPage;
